import React from 'react';
import { X, Settings } from 'lucide-react';
import { ThemeConfig, DashboardConfig, SpaceThing, FavoriteItem } from '../types';

interface DashboardSettingsProps {
  theme: ThemeConfig;
  isOpen: boolean;
  onClose: () => void;
  config: DashboardConfig;
  onConfigChange: (newConfig: DashboardConfig) => void;
  allThings: SpaceThing[];
  allFavorites: FavoriteItem[];
}

const DashboardSettings: React.FC<DashboardSettingsProps> = ({
  theme,
  isOpen,
  onClose,
  config,
  onConfigChange,
  allThings,
  allFavorites,
}) => {
  if (!isOpen) return null;

  const handleThingToggle = (thingId: string) => {
    const selectedThings = config.selectedThings.includes(thingId)
      ? config.selectedThings.filter(id => id !== thingId)
      : [...config.selectedThings, thingId];
    onConfigChange({ ...config, selectedThings });
  };

  const handleFavoriteToggle = (favId: string) => {
    const selectedFavorites = config.selectedFavorites.includes(favId)
      ? config.selectedFavorites.filter(id => id !== favId)
      : [...config.selectedFavorites, favId];
    onConfigChange({ ...config, selectedFavorites });
  };
  
  const handleWidgetToggle = (widget: 'things' | 'favorites') => {
    const visibleWidgets = {
        ...config.visibleWidgets,
        [widget]: !config.visibleWidgets[widget],
    };
    onConfigChange({ ...config, visibleWidgets });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className={`${theme.cardBg} backdrop-blur-lg rounded-2xl p-6 shadow-lg ${theme.shadow} border ${theme.border} w-full max-w-md`}>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${theme.text} flex items-center gap-2`}><Settings /> Dashboard Settings</h2>
          <button onClick={onClose} className={`${theme.text} hover:${theme.accent}`}>
            <X />
          </button>
        </div>

        <div className="space-y-4">
            <div>
                <h3 className={`font-semibold ${theme.text} mb-2`}>Visible Widgets</h3>
                <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.visibleWidgets.things}
                            onChange={() => handleWidgetToggle('things')}
                            className="form-checkbox h-5 w-5 rounded"
                        />
                        <span className={theme.text}>Show "Things"</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={config.visibleWidgets.favorites}
                            onChange={() => handleWidgetToggle('favorites')}
                            className="form-checkbox h-5 w-5 rounded"
                        />
                        <span className={theme.text}>Show "Favorites"</span>
                    </label>
                </div>
            </div>
          <div>
            <h3 className={`font-semibold ${theme.text} mb-2`}>Select "Things" to Display</h3>
            <div className="grid grid-cols-3 gap-2">
              {allThings.map(thing => (
                <label key={thing.id} className={`p-2 rounded-lg cursor-pointer ${config.selectedThings.includes(thing.id) ? `${theme.primary} text-white` : `${theme.secondary}`}`}>
                  <input
                    type="checkbox"
                    checked={config.selectedThings.includes(thing.id)}
                    onChange={() => handleThingToggle(thing.id)}
                    className="sr-only"
                  />
                  <span className="text-sm text-center block">{thing.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <h3 className={`font-semibold ${theme.text} mb-2`}>Select "Favorites" to Display</h3>
            <div className="grid grid-cols-3 gap-2">
              {allFavorites.map(fav => (
                <label key={fav.id} className={`p-2 rounded-lg cursor-pointer ${config.selectedFavorites.includes(fav.id.toString()) ? `${theme.primary} text-white` : `${theme.secondary}`}`}>
                  <input
                    type="checkbox"
                    checked={config.selectedFavorites.includes(fav.id.toString())}
                    onChange={() => handleFavoriteToggle(fav.id.toString())}
                    className="sr-only"
                  />
                  <span className="text-sm text-center block">{fav.name}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSettings;